// @greenlight-spec: 19-design-system.md
// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 44-marketplace-listings.md#enforcement-what-a-lapsed-entitlement-does
// @greenlight-spec: decisions/0084-direct-installs-need-a-payment-method-within-seven-days.md
import * as React from 'react';
import { Link } from 'react-router-dom';
import { CreditCard } from 'lucide-react';

import type { CurrentUser } from '@/lib/auth';
import {
  FLEET_PAYMENT_METHOD_REASONS,
  type BillingEntitlementEnforcement,
} from '@greenlight/shared';

interface BillingEntitlementBannerProps {
  enforcement: BillingEntitlementEnforcement;
  role: CurrentUser['role'];
}

// Written out rather than composed, so Tailwind's scanner sees whole classes.
const TONE = {
  warning: {
    container: 'border-[hsl(var(--status-amber))] bg-[hsl(var(--status-amber)/0.15)]',
    icon: 'text-[hsl(var(--status-amber-text))]',
  },
  degraded: {
    container: 'border-[hsl(var(--status-red))] bg-[hsl(var(--status-red)/0.15)]',
    icon: 'text-[hsl(var(--status-red-text))]',
  },
} as const;

/**
 * Where the admin goes to fix it. Naming a marketplace an install never bought
 * through is worse than saying nothing, so a direct contract points at Shift.
 */
const REMEDY: Record<BillingEntitlementEnforcement['billing_source'], string> = {
  azure: 'Check this subscription in the Azure portal.',
  aws: 'Check this subscription in AWS Marketplace.',
  gcp: 'Check this subscription in Google Cloud Marketplace.',
  direct: 'Contact Shift Engineering to bring this contract back into good standing.',
  none: 'Contact Shift Engineering to bring this contract back into good standing.',
};

const PAYMENT_METHOD_REASONS: ReadonlySet<string> = new Set(FLEET_PAYMENT_METHOD_REASONS);

const LINK_CLASS =
  'inline-flex h-11 shrink-0 items-center text-[13px] font-medium text-link underline-offset-4 hover:underline md:h-9';

/**
 * UTC, not the viewer's zone. A dated term's deadline is a calendar day, and
 * localizing the instant it starts moves the printed date back a day for every
 * viewer west of UTC — on the one date the customer is being asked to act on.
 */
function formatDay(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

/**
 * A marketplace install's admin fixes a subscription; a direct contract's does
 * not have one. Naming the wrong thing is how a banner stops being actionable.
 */
function agreementNoun(source: BillingEntitlementEnforcement['billing_source']): string {
  return source === 'azure' || source === 'aws' || source === 'gcp' ? 'subscription' : 'license';
}

/**
 * The one lapse whose remedy is a page in this dashboard: a Stripe-collected
 * install owes a saved card by a date Shift names from the first report, so
 * the banner asks for it while the install is still entitled, counts down
 * through the warning window, and says what stopped building afterwards.
 */
function PaymentMethodBanner({
  enforcement,
  role,
}: BillingEntitlementBannerProps): React.JSX.Element {
  const degraded = enforcement.state === 'degraded';
  const tone = degraded ? TONE.degraded : TONE.warning;
  const dueDay = enforcement.degrade_at === null ? null : formatDay(enforcement.degrade_at);
  return (
    <div
      role="region"
      aria-label="Billing entitlement status"
      data-testid="billing-entitlement-banner"
      data-state={enforcement.state}
      data-reason={enforcement.entitlement_reason}
      className={`flex flex-wrap items-center gap-x-3 gap-y-1 border-b px-4 py-2 pt-[calc(0.5rem+env(safe-area-inset-top))] md:px-6 ${tone.container}`}
    >
      <CreditCard className={`size-4 shrink-0 ${tone.icon}`} aria-hidden />
      <p className="min-w-0 flex-1 text-[13px] text-foreground">
        <span className="font-semibold">
          {degraded
            ? 'Building is disabled — no payment method is saved for this install.'
            : 'This install needs a payment method.'}
        </span>{' '}
        <span className="text-muted-foreground">
          {degraded
            ? 'Creating and deploying apps is blocked until a card is saved. Running apps keep serving.'
            : dueDay !== null
              ? `Building will be disabled on ${dueDay} unless a card is saved. Running apps keep serving.`
              : 'Building will be disabled unless a card is saved. Running apps keep serving.'}{' '}
          {role === 'org_admin'
            ? 'Add one in Settings → Billing.'
            : 'Ask an org admin to add one in Settings → Billing.'}
        </span>
      </p>
      {role === 'org_admin' && (
        <Link to="/settings?tab=billing" className={LINK_CLASS}>
          Add payment method
        </Link>
      )}
    </div>
  );
}

/**
 * Persistent banner shown on every dashboard route while this install's
 * Greenlight entitlement is lapsed (docs/44 §Enforcement), or while a payment
 * method it owes is still unsaved. Amber counts down to the date building
 * stops; red says it already has. Every role sees it — a builder whose agent is
 * being rejected needs the reason — and only the payment-method case carries a
 * link, because that remedy is a page in this dashboard and the others are a
 * subscription or a contract.
 *
 * Non-dismissable by design: the only thing that clears it is an entitled
 * check, which clears it automatically.
 */
export function BillingEntitlementBanner({
  enforcement,
  role,
}: BillingEntitlementBannerProps): React.JSX.Element | null {
  if (
    enforcement.entitlement_reason !== null &&
    PAYMENT_METHOD_REASONS.has(enforcement.entitlement_reason)
  ) {
    return <PaymentMethodBanner enforcement={enforcement} role={role} />;
  }
  if (enforcement.state === 'ok') return null;

  const degraded = enforcement.state === 'degraded';
  const tone = degraded ? TONE.degraded : TONE.warning;
  const noun = agreementNoun(enforcement.billing_source);
  // `grace` is a term running out, not an agreement that has already failed —
  // telling a customer mid-trial that their license "has lapsed" is both wrong
  // and the kind of thing that generates a support ticket.
  const ending = enforcement.entitlement_state === 'grace';

  return (
    <div
      role="region"
      aria-label="Billing entitlement status"
      data-testid="billing-entitlement-banner"
      data-state={enforcement.state}
      className={`flex flex-wrap items-center gap-x-3 gap-y-1 border-b px-4 py-2 pt-[calc(0.5rem+env(safe-area-inset-top))] md:px-6 ${tone.container}`}
    >
      <CreditCard className={`size-4 shrink-0 ${tone.icon}`} aria-hidden />
      <p className="min-w-0 flex-1 text-[13px] text-foreground">
        <span className="font-semibold">
          {degraded
            ? `Building is disabled — this install’s Greenlight ${noun} has lapsed.`
            : ending
              ? `This install’s Greenlight ${noun} is ending.`
              : `This install’s Greenlight ${noun} has lapsed.`}
        </span>{' '}
        <span className="text-muted-foreground">
          {degraded
            ? 'Creating and deploying apps is blocked until it is active again. Running apps keep serving.'
            : enforcement.degrade_at !== null
              ? `Building will be disabled on ${formatDay(enforcement.degrade_at)}. Running apps keep serving.`
              : 'Building will be disabled if this continues.'}{' '}
          {REMEDY[enforcement.billing_source]}
        </span>
      </p>
    </div>
  );
}
